import React, { FC } from 'react';
import styled from 'styled-components';
import { HormonePatternMetricViewModel } from '../../../common/model/type/hormone-summary-status';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import {
  getLevelPresentation,
  getStabilityPresentation,
  HormonePatternHormone,
  HormonePatternPhase,
} from '../organisms/hormone-summary-presentation';
import OvSummaryStatus from './OvSummaryStatus';

interface OvHormonePatternMetricProps {
  name: 'Level' | 'Stability';
  hormone: HormonePatternHormone;
  phase: HormonePatternPhase;
  metric: HormonePatternMetricViewModel | null;
  unit: string;
  gridArea: string;
  position: 'level' | 'stability';
}

const formatNumber = (value: number): string =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(value);

const formatExpectedRange = (low: number, high: number, unit: string): string =>
  high === Infinity
    ? `Expected ${formatNumber(low)}+ ${unit}`
    : `Expected ${formatNumber(low)}–${formatNumber(high)} ${unit}`;

const OvHormonePatternMetric: FC<OvHormonePatternMetricProps> = ({
  name,
  hormone,
  phase,
  metric,
  unit,
  gridArea,
  position,
}) => {
  const presentation =
    name === 'Level'
      ? getLevelPresentation(
          hormone,
          phase,
          metric?.levelStatus ?? 'insufficient_data'
        )
      : getStabilityPresentation(
          metric?.stabilityStatus ?? 'insufficient_data'
        );

  return (
    <MetricContainer $gridArea={gridArea} $position={position}>
      <MetricName>{name}</MetricName>
      <OvSummaryStatus presentation={presentation} />
      {metric && (
        <Evidence>
          {name === 'Level' ? (
            formatExpectedRange(
              metric.expectedRange.low,
              metric.expectedRange.high,
              unit
            )
          ) : (
            <>
              {metric.percentOfDaysInExpectedRange === null
                ? 'Expected-range percentage unavailable'
                : `${formatNumber(
                    metric.percentOfDaysInExpectedRange
                  )}% of days in range`}
            </>
          )}
        </Evidence>
      )}
    </MetricContainer>
  );
};

export default OvHormonePatternMetric;

const MetricContainer = styled.div<{
  $gridArea: string;
  $position: 'level' | 'stability';
}>`
  grid-area: ${({ $gridArea }) => $gridArea};
  min-width: 0;
  padding: 0.75rem;
  padding-top: 0.875rem;
  background: ${Colours.OV_WHITE};
  border-radius: ${({ $position }) =>
    $position === 'stability'
      ? `0 0 ${Variables.borderRadius.MEDIUM} ${Variables.borderRadius.MEDIUM}`
      : '0'};

  @container hormone-summary (min-width: 460px) {
    border-radius: ${({ $position }) =>
      $position === 'level'
        ? `0 0 0 ${Variables.borderRadius.MEDIUM}`
        : `0 0 ${Variables.borderRadius.MEDIUM} 0`};
  }
`;

const MetricName = styled.span`
  display: block;
  margin-bottom: 0.375rem;
  color: ${Colours.OV_PRUSSIAN_BLUE};
  font-size: ${Variables.fontSizes.SMALL};
  font-weight: bold;
  text-transform: uppercase;
`;

const Evidence = styled.span`
  display: block;
  margin-top: 0.375rem;
  color: ${Colours.OV_PRUSSIAN_BLUE};
  font-size: ${Variables.fontSizes.SMALL};
  line-height: ${Variables.lineHeights.LARGE};
  overflow-wrap: anywhere;
`;
